import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import CompanyTab from "../components/tabs/CompanyTab";
import CompanyAuditTab from "../components/tabs/CompanyAuditTab";
import DocControlTab from "../components/tabs/DocControlTab";
import TeamTab from "../components/tabs/TeamTab";
import { api, formatApiError } from "../lib/api";
import { isCompanyRegistered } from "../lib/company";
import { useT } from "../lib/i18n";

export default function CompanyPage() {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const setupRequested = searchParams.get("setup") === "1";
  const setupRequired = !!profile && !isCompanyRegistered(profile);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/user/profile");
      setProfile(data);
    } catch (requestError) {
      setError(formatApiError(requestError.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function finishSetup() {
    navigate(location.state?.from || "/dashboard?section=regulatory", { replace: true });
  }

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{t("nav.main.company")}</div>
        <h1 className="text-3xl font-extrabold tracking-tight">{t("nav.main.company")}</h1>
        <p className="mt-1 text-sm text-zinc-500">{t("company.settings_sub")}</p>
      </div>

      {loading ? (
        <div className="grid-card p-6 text-sm text-zinc-500">{t("dash.loading")}</div>
      ) : error ? (
        <div className="grid-card p-6 space-y-3" role="alert">
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={load} className="btn-primary px-3 py-2 text-sm">{t("app.retry")}</button>
        </div>
      ) : (
        <>
          {setupRequired && (
            <div className="mb-5 rounded-sm border border-amber-200 bg-amber-50 p-4 sm:p-5" data-testid="company-setup-required">
              <h2 className="font-bold text-amber-900">{t("company.setup.title")}</h2>
              <p className="mt-1 text-sm leading-6 text-amber-900">{t("company.setup.sub")}</p>
            </div>
          )}
          {setupRequested && !setupRequired && (
            <div className="mb-5 flex flex-col gap-3 rounded-sm border border-emerald-200 bg-emerald-50 p-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm font-semibold text-emerald-900">{t("company.setup.complete")}</p>
              <button onClick={finishSetup} className="btn-primary px-4 py-2 text-sm" data-testid="company-setup-continue">
                {t("company.setup.continue")}
              </button>
            </div>
          )}

          <div
            className={setupRequired ? "" : "grid grid-cols-1 gap-5 xl:grid-cols-2 xl:items-start"}
            data-testid="company-top-layout"
          >
            <div data-testid="company-fixed-blocks">
              <CompanyTab
                requiredSetup={setupRequired}
                onProfileUpdate={setProfile}
                onSetupComplete={finishSetup}
              />
            </div>
            {!setupRequired && (
              <div data-testid="company-facility-audit-top">
                <CompanyAuditTab />
              </div>
            )}
          </div>

          {!setupRequired && (
            <section className="mt-8" data-testid="company-team-primary">
              <TeamTab />
            </section>
          )}

          {!setupRequired && (
            <section className="mt-8" data-testid="company-document-control-lower">
              <DocControlTab />
            </section>
          )}
        </>
      )}
    </AppShell>
  );
}
